import {
  Box,
  Modal,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import React, {useState, useEffect, useCallback} from 'react';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';

// Predefined dynamic entity configurations
const DYNAMIC_ENTITY_CONFIGS = [
  {
    id: 'product',
    name: 'Product',
    route: '/api/products',
    description: 'For product detail pages',
  },
  {
    id: 'valuepack',
    name: 'ValuePack',
    route: '/api/valuepacks',
    description: 'For valuepack detail pages',
  },
  {
    id: 'category',
    name: 'Category',
    route: '/api/categories',
    description: 'For category detail pages',
  },
];

const GeneralModal = ({
  open = false,
  onClose = () => {},
  onSubmitPageData = () => {},
  register,
  handleSubmit,
  errors,
  formData, 
  watch,
  setValue,
  isEditing = false,
}) => {
  const [active, setActive] = useState(false);
  const [preview, setPreview] = useState(null);
  const [routeType, setRouteType] = useState('STATIC');
  const [selectedDynamicEntities, setSelectedDynamicEntities] = useState([]);

  // Reset all states when modal opens/closes or mode changes
  useEffect(() => {
    if (open) {
      if (isEditing && formData) {
        // EDIT MODE - Set values from formData
        setActive(formData.active ?? false);
        setPreview(formData.image_url || null);
        setRouteType(formData.pageType || 'STATIC');

        // Set dynamic entities if they exist
        if (formData.dynamicEntities && formData.dynamicEntities.length > 0) {
          setSelectedDynamicEntities(formData.dynamicEntities);
          // Also set in react-hook-form
          setValue('dynamicEntities', formData.dynamicEntities);
        } else {
          setSelectedDynamicEntities([]);
          setValue('dynamicEntities', []);
        }

        // Set pageType in react-hook-form
        setValue('pageType', formData.pageType || 'STATIC');
      } else {
        // CREATE MODE - Reset to defaults
        setActive(true); // Default active for new pages
        setPreview(null);
        setRouteType('STATIC');
        setSelectedDynamicEntities([]);
        setValue('pageType', 'STATIC');
        setValue('dynamicEntities', []);
      }
    }
  }, [open, isEditing, formData, setValue]);

  // Auto-detect route type when route changes (only for create mode)
  useEffect(() => {
    const currentRoute = watch('route');
    if (currentRoute && !isEditing) {
      const detectedType = currentRoute.includes(':') ? 'DYNAMIC' : 'STATIC';
      setRouteType(detectedType);
      setValue('pageType', detectedType);

      // Clear dynamic entities when switching to static
      if (detectedType === 'STATIC') {
        setSelectedDynamicEntities([]);
        setValue('dynamicEntities', []);
      }
    }
  }, [watch('route'), setValue, isEditing]);

  // Cleanup preview on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleFormSubmit = useCallback(
    data => {
      // Merge form data with local state
      const formValues = {
        ...data,
        active: active,
        pageType: routeType,
        dynamicEntities: selectedDynamicEntities,
      };

      console.log('Submitting form:', formValues);
      onSubmitPageData(formValues);
    },
    [active, routeType, selectedDynamicEntities, onSubmitPageData],
  );

  const handleDynamicEntityChange = event => {
    const selectedIds = event.target.value;
    const selectedEntities = DYNAMIC_ENTITY_CONFIGS.filter(entity =>
      selectedIds.includes(entity.id),
    );
    setSelectedDynamicEntities(selectedEntities);
    setValue('dynamicEntities', selectedEntities);
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  const handleImageChange = useCallback(
    e => {
      const file = e.target.files[0];
      if (!file) return;

      // Clean up previous preview
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }

      const newPreview = URL.createObjectURL(file);
      setPreview(newPreview);
      setValue('image', file, {shouldValidate: false});
    },
    [preview, setValue],
  );

  // Route examples for helper text
  const routeExamples = {
    STATIC: ['/home', '/about', '/contact'],
    DYNAMIC: ['/product/:id', '/user/:userId', '/category/:slug'],
  };

  return (
    <Modal
      closeAfterTransition
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      slots={{backdrop: Backdrop}}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}>
      <Box sx={style}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          gutterBottom>
          {isEditing ? 'Edit Page' : 'Create New Page'}
        </Typography>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Page Name Field */}
          <TextField
            label="Page Name"
            variant="outlined"
            fullWidth
            required
            sx={{mt: 2}}
            {...register('name', {required: 'Name is required'})}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ''}
            // Use key to force re-render when switching modes
            key={isEditing ? 'edit-name' : 'create-name'}
          />

          {/* Route Field */}
          <TextField
            label="Route"
            variant="outlined"
            fullWidth
            required
            sx={{mt: 2}}
            {...register('route', {
              required: 'Route is required',
              pattern: {
                value: /^\/[a-zA-Z0-9\-_\/:]+$/,
                message: 'Invalid route format. Use /path or /path/:param',
              },
            })}
            error={!!errors.route}
            helperText={
              errors.route
                ? errors.route.message
                : `Examples: ${routeExamples[routeType].join(', ')}`
            }
            placeholder={routeType === 'STATIC' ? '/about' : '/product/:id'}
            key={isEditing ? 'edit-route' : 'create-route'}
          />

          {/* Route Type Display */}
          <MDBox
            sx={{
              mt: 1,
              p: 1,
              bgcolor: routeType === 'DYNAMIC' ? 'warning.light' : 'info.light',
              borderRadius: 1,
            }}>
            <Typography variant="body2">
              <strong>Page Type:</strong> {routeType}
              {routeType === 'DYNAMIC' && (
                <Chip
                  label="Dynamic Route"
                  color="warning"
                  size="small"
                  sx={{ml: 1}}
                />
              )}
            </Typography>
          </MDBox>

          {/* Dynamic Entities - Only for dynamic routes */}
          {routeType === 'DYNAMIC' && (
            <FormControl fullWidth sx={{mt: 2}}>
              <InputLabel>Dynamic Entities</InputLabel>
              <Select
                multiple
                value={selectedDynamicEntities.map(entity => entity.id)}
                onChange={handleDynamicEntityChange}
                renderValue={selected => (
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                    {selected.map(value => {
                      const entity = DYNAMIC_ENTITY_CONFIGS.find(
                        e => e.id === value,
                      );
                      return (
                        <Chip
                          key={value}
                          label={entity?.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
                label="Dynamic Entities">
                {DYNAMIC_ENTITY_CONFIGS.map(entity => (
                  <MenuItem key={entity.id} value={entity.id}>
                    <Typography variant="body1">{entity.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select entity types this dynamic page will support
              </FormHelperText>
            </FormControl>
          )}

          {/* Image Input */}
          <div style={{marginTop: '16px'}}>
            {preview && (
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: '100px',
                  height: '100px',
                  objectFit: 'cover',
                  borderRadius: '5px',
                  marginBottom: '10px',
                  display: 'block',
                }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              name="image"
              style={{marginBottom: '10px'}}
            />
            {errors.image && (
              <p style={{color: 'red', margin: '5px 0'}}>
                {errors.image.message}
              </p>
            )}
          </div>

          {/* Active Switch */}
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={e => setActive(e.target.checked)}
                name="active"
                color="primary"
              />
            }
            label="Active"
            sx={{mt: 2, display: 'block'}}
          />

          {/* Submit Button */}
          <MDButton
            type="submit"
            variant="contained"
            color={isEditing ? 'primary' : 'info'}
            fullWidth
            sx={{mt: 3}}>
            {isEditing ? 'Update Page' : 'Create Page'}
          </MDButton>
        </form>
      </Box>
    </Modal>
  );
};

export default GeneralModal;
